import { NoFileList } from "./NoFileList";
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const Row = ({ label, r, testid }) => (
  <li className="flex items-start gap-2 text-sm" data-testid={testid}>
    {r.ok ? <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /> : <XCircle size={16} className="text-red-600 mt-0.5 shrink-0" />}
    <span><b className="font-semibold">{label}</b> <span className="text-neutral-600">{r.detail}</span></span>
  </li>
);

// Live health of the pieces video posters/playback depend on (ffmpeg, OneDrive, cache disk, object storage).
export const ServerHealth = () => {
  const [h, setH] = useState(null);
  const load = () => api.get("/admin/health").then(({ data }) => setH(data)).catch(() => setH(false));
  useEffect(() => { load(); }, []);
  useEffect(() => {  // follow encryption progress
    if (!h?.encryption?.running) return undefined;
    const t = setInterval(() => api.get("/admin/encryption").then(({ data }) => setH((x) => ({ ...x, encryption: data }))), 4000);
    return () => clearInterval(t);
  }, [h?.encryption?.running]);
  return (
    <section className="settings-card" data-testid="server-health">
      <h2 className="text-base font-semibold text-neutral-900">Server health</h2>
      {h === null ? <p className="text-sm text-neutral-500 mt-2"><Loader2 size={14} className="animate-spin inline" /> Checking…</p>
        : h === false ? <p className="text-sm text-red-600 mt-2">Couldn't check right now.</p> : (
          <ul className="mt-3 space-y-1.5">
            <Row label="ffmpeg" r={h.ffmpeg} testid="health-ffmpeg" />
            {h.nofile && <Row label="Stored files" r={h.nofile} testid="health-nofile" />}
            {h.nofile?.count > 0 && <NoFileList onChange={load} />}
            {h.onedrive && <Row label="OneDrive" testid="health-onedrive" r={{ ok: h.onedrive.ok, detail: h.onedrive.detail
              + (h.onedrive.quota_used != null ? ` · ${(h.onedrive.quota_used / 1e9).toFixed(1)} GB used${h.onedrive.quota_free != null ? `, ${(h.onedrive.quota_free / 1e9).toFixed(1)} GB free` : ""}` : "") }} />}
            {h.objstore_note && <p className="text-xs text-neutral-500" data-testid="health-objstore-note">{h.objstore_note}</p>}
            <Row label="Media cache disk" r={h.cache} testid="health-cache" />
            <Row label="Thumbnail storage" r={h.storage} testid="health-storage" />
            <Row label="AI tagging" r={h.ai} testid="health-ai" />
            <Row label="Email" r={h.email} testid="health-email" />
            <Row label="Video posters" r={{ ok: !h.videos_missing_poster, detail: h.videos_missing_poster ? `${h.videos_missing_poster} missing, retrying in background` : "all present" }} testid="health-posters" />
            <Row label="Encryption" testid="health-encryption" r={{ ok: h.encryption.key_present && !h.encryption.pending,
              detail: h.encryption.key_present
                ? `key ${h.encryption.fingerprint} · ${h.encryption.encrypted} private encrypted · ${h.encryption.pending} not yet${h.encryption.running ? " (encrypting…)" : ""}`
                : "KEY MISSING: encrypted Private originals show “temporarily unavailable”. Add PRIVATE_ENC_KEY in Secrets." }} />
          </ul>
        )}
      {h?.encryption?.key_present && h.encryption.pending > 0 && (
        <button className="btn-outline mt-3 inline-flex items-center gap-2" disabled={h.encryption.running}
          onClick={() => api.post("/admin/encryption/run").then(({ data }) => setH({ ...h, encryption: { ...data, running: true } }))} data-testid="encrypt-private-now">
          {h.encryption.running ? <Loader2 size={14} className="animate-spin" /> : null} Encrypt private photos now
        </button>
      )}
    </section>
  );
};
